import { Link } from 'react-router-dom'
import { FadeIn, GlowOrb, CountUp, Shimmer } from '../components/animations'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Ticket Counter Homepage
   Dark, editorial landing page. Clearly identifies the problem and solution.
   ═══════════════════════════════════════════════════════════════════════════ */

export default function HomePage() {
  return (
    <div className="relative min-h-screen bg-[#0a0a0f] overflow-hidden" style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
      {/* Ambient glow orbs */}
      <GlowOrb color="rgba(147, 51, 234, 0.08)" size={800} className="top-[-200px] left-[-200px]" duration={25} />
      <GlowOrb color="rgba(56, 189, 248, 0.06)" size={600} className="top-[40%] right-[-150px]" duration={30} />
      <GlowOrb color="rgba(245, 158, 11, 0.05)" size={500} className="bottom-[-100px] left-[30%]" duration={22} />

      {/* ── Navigation ─────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06] bg-[#0a0a0f]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-screen-2xl items-center justify-between px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/favicon.png" alt="DIZEE" className="w-7 h-7" />
              <span className="text-white font-semibold text-sm tracking-tight hidden sm:inline">DIZEE</span>
            </Link>
            <span className="hidden sm:inline-flex items-center rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-medium text-purple-400">
              Ticket Counter
            </span>
          </div>
          <div className="flex items-center gap-1">
            <NavLink href="#features">Features</NavLink>
            <NavLink href="#how-it-works">How It Works</NavLink>
            <NavLink href="#pricing">Pricing</NavLink>
            <NavLink href="#faq">FAQ</NavLink>
            <div className="hidden md:flex items-center ml-4 pl-4 border-l border-white/[0.06] gap-2">
              <Link
                to="/login"
                className="inline-flex items-center px-3.5 py-1.5 text-[13px] font-medium text-gray-300 hover:text-white border border-white/[0.08] rounded-lg hover:border-white/[0.15] hover:bg-white/[0.04] transition-all"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-purple-500 transition-colors"
              >
                Get started
              </Link>
            </div>
            {/* Mobile menu */}
            <div className="flex md:hidden items-center gap-2 ml-2">
              <Link
                to="/login"
                className="inline-flex items-center px-3 py-1.5 text-[12px] font-medium text-gray-300 border border-white/[0.08] rounded-lg"
              >
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* ── Hero ────────────────────────────────────────────────────── */}
        <section className="relative pt-32 pb-24 sm:pt-40 sm:pb-32">
          <div className="mx-auto max-w-screen-2xl px-6 lg:px-8">
            <FadeIn delay={100}>
              <p className="text-sm font-medium text-purple-400 mb-6">
                The single source of truth for ticket counts
              </p>
            </FadeIn>
            <FadeIn delay={200}>
              <h1 className="text-5xl sm:text-7xl lg:text-8xl font-semibold tracking-tighter text-white text-balance max-w-5xl leading-[0.95]">
                Stop chasing ticket counts through email.
              </h1>
            </FadeIn>
            <FadeIn delay={350}>
              <p className="mt-8 text-base sm:text-lg text-gray-400 max-w-2xl leading-relaxed">
                Ticket counts are scattered across emails, spreadsheets, and phone calls.
                Three people on your team need the numbers and they all ask separately.
                By the time anyone updates the sheet, the data is stale.{' '}
                <span className="text-gray-300">Dizee fixes that.</span>{' '}
                One place for every ticket count, every show, every team member.
              </p>
            </FadeIn>
            <FadeIn delay={450}>
              <div className="mt-10 flex items-center gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-3 text-sm font-semibold text-white hover:bg-purple-500 transition-colors shadow-lg shadow-purple-600/20"
                >
                  Get started free
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center rounded-lg border border-white/[0.12] bg-white/[0.05] px-6 py-3 text-sm font-medium text-gray-200 hover:text-white hover:border-white/[0.25] hover:bg-white/[0.08] transition-all"
                >
                  Sign in
                </Link>
              </div>
            </FadeIn>

            {/* ── Stats bar ──────────────────────────────────────────── */}
            <FadeIn delay={550} className="mt-20">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.04] rounded-xl overflow-hidden border border-white/[0.06]">
                <StatCard label="Hours saved per week" value={<CountUp target={12} suffix="+" />} />
                <StatCard label="Email threads eliminated" value={<CountUp target={95} suffix="%" />} />
                <StatCard label="Team members in sync" value="Everyone" />
                <StatCard label="Monthly cost" value="$0" />
              </div>
            </FadeIn>

          </div>
        </section>

        {/* ── Built for ──────────────────────────────────────────────── */}
        <section className="relative border-t border-white/[0.06] py-16">
          <div className="mx-auto max-w-screen-2xl px-6 lg:px-8">
            <FadeIn>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-600 mb-8">
                Built for the people who actually need ticket counts
              </p>
            </FadeIn>
            <FadeIn delay={150}>
              <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
                {['Booking Agents', 'Artist Managers', 'Tour Managers', 'Promoters', 'Label A&R', 'Business Managers'].map((role) => (
                  <span
                    key={role}
                    className="text-[15px] font-medium tracking-tight text-gray-500 hover:text-gray-300 transition-colors cursor-default select-none"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ── Problem ────────────────────────────────────────────────── */}
        <Section id="problem">
          <div className="max-w-3xl">
            <FadeIn>
              <SectionTag>The real cost of "I'll check the email"</SectionTag>
            </FadeIn>
            <FadeIn delay={100}>
              <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tighter text-white text-balance">
                Ticket counts are broken.{' '}
                <Shimmer className="text-purple-400">Everyone knows it.</Shimmer>
              </h2>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="mt-6 text-base text-gray-400 leading-relaxed">
                Every week it's the same routine. Someone needs the ticket count.
                They email the promoter. The promoter emails back — maybe today,
                maybe tomorrow. You copy the number into a spreadsheet. By the time
                your manager asks for the update, the numbers are already stale.
                Meanwhile, three other people on your team are running the same
                drill independently. Nobody has the same number at the same time.
              </p>
            </FadeIn>
            <div className="mt-10 grid sm:grid-cols-2 gap-x-12 gap-y-6">
              <FadeIn delay={250}>
                <ProblemCard>
                  Your agent emails the promoter for ticket counts. The promoter responds a day later. You update a spreadsheet. By then, the numbers are already old.
                </ProblemCard>
              </FadeIn>
              <FadeIn delay={330}>
                <ProblemCard>
                  Three people on your team need ticket counts. They all ask separately. Nobody has the same number at the same time.
                </ProblemCard>
              </FadeIn>
              <FadeIn delay={410}>
                <ProblemCard>
                  You're making routing and marketing decisions based on week-old data because nobody updated the spreadsheet.
                </ProblemCard>
              </FadeIn>
              <FadeIn delay={490}>
                <ProblemCard>
                  Sellout potential is a guess because you can't see the pace in real time. You find out a show is underselling when it's too late to fix it.
                </ProblemCard>
              </FadeIn>
            </div>
          </div>
        </Section>

        {/* ── Solution ───────────────────────────────────────────────── */}
        <Section id="solution">
          <div className="max-w-3xl">
            <FadeIn>
              <SectionTag>One place for every number</SectionTag>
            </FadeIn>
            <FadeIn delay={100}>
              <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tighter text-white text-balance">
                Input once. Everyone sees it.{' '}
                <span className="text-purple-400">Instantly.</span>
              </h2>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="mt-6 text-base text-gray-400 leading-relaxed">
                Dizee replaces the email chains, phone calls, and spreadsheets with
                a single dashboard. When a ticket count gets updated, everyone on your
                team sees it immediately. No more asking "what's the count?" — just open
                Dizee. Track pace, capacity, percentage sold, and guarantees across every
                show on your tour, in one view.
              </p>
            </FadeIn>
            <div className="mt-10 grid sm:grid-cols-2 gap-x-12 gap-y-6">
              <FadeIn delay={250}>
                <Principle title="Single source of truth">
                  Every ticket count lives in one place. When someone updates a number,
                  your whole team sees it. No more conflicting spreadsheets or stale emails.
                </Principle>
              </FadeIn>
              <FadeIn delay={330}>
                <Principle title="Real-time visibility">
                  See ticket counts, percentage sold, venue capacity, and pace across
                  every show on your tour. Make decisions with current data, not last week's.
                </Principle>
              </FadeIn>
              <FadeIn delay={410}>
                <Principle title="Import what you already have">
                  Pull in shows from CSV, Bandsintown, or add them manually. Dizee works
                  with your existing data — no re-entering everything from scratch.
                </Principle>
              </FadeIn>
              <FadeIn delay={490}>
                <Principle title="Built for teams">
                  Agents, managers, and team members all access the same data. Set
                  permissions per person. Stop duplicating work across your organization.
                </Principle>
              </FadeIn>
            </div>
          </div>
        </Section>

        {/* ── Features ───────────────────────────────────────────────── */}
        <Section id="features">
          <FadeIn>
            <SectionTag>Everything you need, nothing you don't</SectionTag>
          </FadeIn>
          <FadeIn delay={100}>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tighter text-white text-balance">
              Built for ticket counts.{' '}
              <span className="text-purple-400">Nothing else in the way.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="mt-6 text-base text-gray-400 leading-relaxed max-w-2xl">
              No bloated software with 50 features you'll never use. Dizee's ticket
              counter is focused on the one thing that matters: getting your team
              accurate ticket counts, fast.
            </p>
          </FadeIn>

          <FadeIn delay={300}>
            <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.04] rounded-xl overflow-hidden border border-white/[0.06]">
              <FeatureCard title="Shows Dashboard" icon="shows">
                Add shows manually, import from CSV, or pull from Bandsintown. See every
                show with ticket counts, capacity, % sold, and status at a glance.
              </FeatureCard>
              <FeatureCard title="Ticket Counter" icon="ticket">
                Update ticket counts for any show in seconds. Auto-calculates percentage
                sold. Save individual shows or update the entire tour at once.
              </FeatureCard>
              <FeatureCard title="Schedule" icon="calendar">
                Calendar view of all your upcoming shows. Connect Google Calendar
                to keep everything in sync automatically.
              </FeatureCard>
              <FeatureCard title="Team Access" icon="team">
                Invite your whole team. Everyone sees the same data. Set permissions
                so the right people have the right access.
              </FeatureCard>
              <FeatureCard title="Guides" icon="guides">
                Step-by-step walkthroughs for every feature. From adding your first
                show to reading ticket pace data — we've got you covered.
              </FeatureCard>
              <FeatureCard title="Smart Defaults" icon="settings">
                Auto-fill cities and states, remember your venues, calculate percentages
                automatically. Less typing, more tracking.
              </FeatureCard>
            </div>
          </FadeIn>
        </Section>

        {/* ── How It Works ───────────────────────────────────────────── */}
        <Section id="how-it-works">
          <FadeIn>
            <SectionTag>Up and running in minutes</SectionTag>
          </FadeIn>
          <FadeIn delay={100}>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tighter text-white text-balance">
              Three steps. That's it.
            </h2>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="mt-6 text-base text-gray-400 leading-relaxed max-w-2xl">
              No complex setup. No onboarding calls. No waiting for a sales rep.
              Create your account, add your shows, and start tracking ticket counts
              today.
            </p>
          </FadeIn>

          <div className="mt-16 grid lg:grid-cols-3 gap-6">
            <FadeIn delay={100}>
              <StepCard
                num="01"
                title="Add your shows"
                description="Import from CSV, pull from Bandsintown, or add manually. If you already have a spreadsheet, we'll map it automatically."
              />
            </FadeIn>
            <FadeIn delay={200}>
              <StepCard
                num="02"
                title="Input ticket counts"
                description="As you get ticket counts from promoters or box offices, enter them in Dizee. One click, one number, done."
              />
            </FadeIn>
            <FadeIn delay={300}>
              <StepCard
                num="03"
                title="Share with your team"
                description="Invite your team members. Everyone sees real-time ticket counts, capacity, and pace without asking anyone."
              />
            </FadeIn>
          </div>
        </Section>

        {/* ── Pricing ───────────────────────────────────────────────── */}
        <Section id="pricing">
          <FadeIn>
            <SectionTag>Simple, transparent pricing</SectionTag>
          </FadeIn>
          <FadeIn delay={100}>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tighter text-white text-balance">
              Free while in beta. <span className="text-purple-400">Seriously.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="mt-6 text-base text-gray-400 leading-relaxed max-w-2xl">
              We're launching the ticket counter as a free tool while we build out the
              full platform. Get in early, give us feedback, and lock in early-adopter
              pricing when we launch paid tiers.
            </p>
          </FadeIn>
          <FadeIn delay={300}>
            <div className="mt-12 max-w-md">
              <div className="rounded-xl border border-purple-500/20 bg-purple-500/[0.04] p-8">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-4xl font-bold text-white">$0</span>
                  <span className="text-sm text-gray-500">/ month</span>
                </div>
                <p className="text-sm text-gray-400 mb-6">Free during beta — no credit card required</p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Unlimited shows',
                    'Unlimited team members',
                    'CSV & Bandsintown import',
                    'Google Calendar sync',
                    'Real-time ticket counts',
                    'Team permissions',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2.5 text-[13px] text-gray-300">
                      <span className="text-purple-400 flex-shrink-0">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-6 py-3 text-sm font-semibold text-white hover:bg-purple-500 transition-colors shadow-lg shadow-purple-600/20"
                >
                  Get started free
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </FadeIn>
        </Section>

        {/* ── Before / After ─────────────────────────────────────────── */}
        <Section id="compare">
          <FadeIn>
            <SectionTag>The difference is night and day</SectionTag>
          </FadeIn>
          <FadeIn delay={100}>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tighter text-white text-balance">
              What changes when your team uses Dizee.
            </h2>
          </FadeIn>

          <div className="mt-16 grid lg:grid-cols-2 gap-6">
            <FadeIn delay={100} className="flex">
              <div className="flex-1 rounded-xl border border-red-500/20 bg-red-500/[0.03] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-red-500/10">
                  <span className="text-[11px] font-semibold text-red-400/80">WITHOUT DIZEE</span>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    'Email the promoter. Wait 1-2 days for a response.',
                    'Copy the number into a spreadsheet.',
                    'Three team members email separately for the same count.',
                    'Manager asks for an update — the sheet is 5 days old.',
                    'Discover a show is underselling when it\'s too late.',
                    'No one knows what "good pace" looks like.',
                  ].map((item, i) => (
                    <p key={i} className="flex items-start gap-2 text-[13px] text-red-300/60 leading-relaxed">
                      <span className="text-red-400/40 mt-0.5 flex-shrink-0">✕</span>
                      {item}
                    </p>
                  ))}
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={200} className="flex">
              <div className="flex-1 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.03] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-emerald-500/10">
                  <span className="text-[11px] font-semibold text-emerald-400/80">WITH DIZEE</span>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    'Get the ticket count. Enter it in Dizee. Done.',
                    'Everyone on your team sees it immediately.',
                    'No more duplicate emails or phone calls.',
                    'Manager opens Dizee — the data is always current.',
                    'See underselling shows early. Adjust marketing in time.',
                    'Track pace, capacity, and % sold at a glance.',
                  ].map((item, i) => (
                    <p key={i} className="flex items-start gap-2 text-[13px] text-emerald-300/80 leading-relaxed">
                      <span className="text-emerald-400 mt-0.5 flex-shrink-0">✓</span>
                      {item}
                    </p>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </Section>

        {/* ── FAQ ─────────────────────────────────────────────────────── */}
        <Section id="faq">
          <FadeIn>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-white text-balance">
              Frequently asked questions.
            </h2>
          </FadeIn>
          <div className="mt-12 max-w-2xl space-y-8">
            <FadeIn delay={100}>
              <FAQ q="How is this different from a spreadsheet?">
                A spreadsheet can store numbers, but it can't give your whole team
                real-time access to the same data without version conflicts. Dizee is
                purpose-built for ticket counts — auto-calculating percentages, tracking
                pace, and giving everyone on your team a live view without asking anyone.
              </FAQ>
            </FadeIn>
            <FadeIn delay={150}>
              <FAQ q="Can I import my existing tour data?">
                Yes. Import shows from CSV files (export from your booking agency or
                spreadsheet), pull events from Bandsintown, or add shows manually.
                We auto-map common column formats so you're not re-typing anything.
              </FAQ>
            </FadeIn>
            <FadeIn delay={200}>
              <FAQ q="How many team members can I add?">
                Add your whole team. Agents, managers, support staff — everyone who
                needs access to ticket counts can be on Dizee with appropriate permissions.
              </FAQ>
            </FadeIn>
            <FadeIn delay={250}>
              <FAQ q="Does this replace my booking software?">
                Dizee is focused specifically on ticket count tracking and visibility.
                It works alongside your existing booking tools — not as a replacement.
                Think of it as the missing piece that makes ticket counts accessible
                to your entire team.
              </FAQ>
            </FadeIn>
            <FadeIn delay={300}>
              <FAQ q="Is my data secure?">
                Yes. All data is encrypted and access is controlled through authentication
                and team permissions. Only people you invite can see your ticket counts.
              </FAQ>
            </FadeIn>
          </div>
        </Section>

        {/* ── Final CTA ──────────────────────────────────────────────── */}
        <section className="relative py-32">
          <div className="mx-auto max-w-screen-2xl px-6 lg:px-8">
            <FadeIn>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white text-balance">
                Your team deserves better than spreadsheets.
              </h2>
            </FadeIn>
            <FadeIn delay={100}>
              <p className="mt-6 text-base text-gray-400 max-w-xl">
                Stop chasing ticket counts through email. Stop maintaining spreadsheets
                that are stale by the time you share them. Give your entire team one
                place to see every ticket count, every show, in real time.
              </p>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-3 text-sm font-semibold text-white hover:bg-purple-500 transition-colors shadow-lg shadow-purple-600/20"
                >
                  Get started free
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/[0.12] bg-white/[0.05] px-6 py-3 text-sm font-semibold text-gray-200 hover:text-white hover:border-white/[0.25] hover:bg-white/[0.08] transition-all"
                >
                  Sign in to your account
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <footer className="border-t border-white/[0.06]">
          <div className="mx-auto max-w-screen-2xl px-6 lg:px-8 py-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-4">Product</p>
                <ul className="space-y-2.5">
                  <FooterLink to="#features">Features</FooterLink>
                  <FooterLink to="#how-it-works">How It Works</FooterLink>
                  <FooterLink to="#pricing">Pricing</FooterLink>
                  <FooterLink to="#faq">FAQ</FooterLink>
                </ul>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-4">Account</p>
                <ul className="space-y-2.5">
                  <FooterLink to="/signup">Get Started</FooterLink>
                  <FooterLink to="/login">Sign In</FooterLink>
                </ul>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-4">Legal</p>
                <ul className="space-y-2.5">
                  <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
                  <FooterLink to="/terms-of-service">Terms of Service</FooterLink>
                  <FooterLink to="/accessibility-statement">Accessibility</FooterLink>
                </ul>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-4">Built for</p>
                <ul className="space-y-2.5">
                  <li className="text-[13px] text-gray-600">Booking Agents</li>
                  <li className="text-[13px] text-gray-600">Artist Managers</li>
                  <li className="text-[13px] text-gray-600">Tour Managers</li>
                  <li className="text-[13px] text-gray-600">Promoters</li>
                </ul>
              </div>
            </div>
            <div className="mt-16 pt-8 border-t border-white/[0.06] flex items-center justify-between">
              <p className="text-[13px] text-gray-600">
                &copy; {new Date().getFullYear()} Dizee
              </p>
              <p className="text-[13px] text-gray-700">
                Ticket counts, simplified.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Local Components
   ═══════════════════════════════════════════════════════════════════════════ */

function Section({
  id,
  children,
}: {
  id: string
  children: React.ReactNode
}) {
  return (
    <section id={id} className="relative border-t border-white/[0.06]">
      <div className="mx-auto max-w-screen-2xl px-6 lg:px-8 py-24 sm:py-32">
        {children}
      </div>
    </section>
  )
}

function SectionTag({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-medium text-purple-400">{children}</p>
  )
}

function ProblemCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-red-500/10 bg-red-500/[0.03] px-4 py-3">
      <p className="text-[13px] text-red-300/60 leading-relaxed">{children}</p>
    </div>
  )
}

function Principle({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[13px] font-semibold text-white">{title}</h3>
      <p className="mt-1.5 text-[13px] text-gray-500 leading-relaxed">{children}</p>
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-[#0a0a0f] p-6">
      <div className="text-2xl font-semibold text-white mb-1">{value}</div>
      <div className="text-[12px] text-gray-500">{label}</div>
    </div>
  )
}

const FEATURE_ICONS: Record<string, JSX.Element> = {
  shows: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3M5 11h14M6 21h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  ticket: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  ),
  calendar: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3M5 11h14M6 21h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 16h6" />
    </svg>
  ),
  team: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  guides: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  settings: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35A1.724 1.724 0 005.682 8.93c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
}

function FeatureCard({
  title,
  icon,
  children,
}: {
  title: string
  icon: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-[#0a0a0f] p-6 lg:p-8 hover:bg-white/[0.02] transition-colors">
      <div className="text-purple-400 mb-3">{FEATURE_ICONS[icon]}</div>
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[13px] text-gray-400 leading-relaxed">{children}</p>
    </div>
  )
}

function StepCard({
  num,
  title,
  description,
}: {
  num: string
  title: string
  description: string
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 hover:bg-white/[0.04] transition-colors group relative overflow-hidden">
      <div className="absolute top-4 right-4 text-[40px] font-bold text-white/[0.03] leading-none select-none">
        {num}
      </div>
      <span className="text-[11px] font-mono text-purple-400/60 mb-3 block">{num}</span>
      <h3 className="text-[15px] font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">{title}</h3>
      <p className="text-[13px] text-gray-400 leading-relaxed">{description}</p>
    </div>
  )
}

function FAQ({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-white/[0.06] pb-8">
      <h3 className="text-[15px] font-semibold text-white">{q}</h3>
      <p className="mt-3 text-[13px] text-gray-400 leading-relaxed">{children}</p>
    </div>
  )
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="hidden md:inline-flex items-center px-3 py-1.5 text-[13px] text-gray-400 hover:text-white transition-colors rounded-md"
    >
      {children}
    </a>
  )
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  const isExternal = to.startsWith('#')
  if (isExternal) {
    return (
      <li>
        <a href={to} className="text-[13px] text-gray-500 hover:text-gray-300 transition-colors">
          {children}
        </a>
      </li>
    )
  }
  return (
    <li>
      <Link to={to} className="text-[13px] text-gray-500 hover:text-gray-300 transition-colors">
        {children}
      </Link>
    </li>
  )
}
